import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';
export type ColorScheme = 'default' | 'purple' | 'green' | 'blue' | 'orange';

interface ThemeState {
  theme: Theme;
  colorScheme: ColorScheme;
  sidebarCollapsed: boolean;
  searchQuery: string;
  setTheme: (theme: Theme) => void;
  setColorScheme: (scheme: ColorScheme) => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  initializeSidebar: () => void;
  setSearchQuery: (query: string) => void;
}

export const useThemeStore = create<ThemeState>()(
  persist(
    set => ({
      theme: 'system',
      colorScheme: 'default',
      sidebarCollapsed: false,
      searchQuery: '',

      setTheme: (theme: Theme) => {
        set({ theme });
        // Apply theme to document
        if (theme === 'dark') {
          document.documentElement.classList.add('dark');
        } else if (theme === 'light') {
          document.documentElement.classList.remove('dark');
        } else {
          // System theme
          const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
          if (prefersDark) {
            document.documentElement.classList.add('dark');
          } else {
            document.documentElement.classList.remove('dark');
          }
        }
      },

      setColorScheme: (colorScheme: ColorScheme) => {
        set({ colorScheme });
      },

      toggleSidebar: () => {
        set(state => ({ sidebarCollapsed: !state.sidebarCollapsed }));
      },

      setSidebarCollapsed: (sidebarCollapsed: boolean) => {
        set({ sidebarCollapsed });
      },

      initializeSidebar: () => {
        // Auto-collapse sidebar on mobile/tablet
        const isMobile = window.innerWidth < 1024;
        if (isMobile) {
          set({ sidebarCollapsed: true });
        }
      },

      setSearchQuery: (searchQuery: string) => {
        set({ searchQuery });
      },
    }),
    {
      name: 'theme-storage',
    }
  )
);
